import { useCallback, useEffect, useMemo, useState } from "react";
import { Clock, WarningCircle, Gauge, Truck, CalendarBlank, Timer, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATE_LABEL = {
  late: "Late",
  pickup_overdue: "Not collected",
  at_risk: "At risk",
  on_track: "On track",
  delivered_late: "Delivered late",
  closed: "Closed",
};
const STATE_TONE = {
  late: "border-[#B3241B] bg-[#FFECEB] text-[#7A1712]",
  pickup_overdue: "border-[#B3241B] bg-[#FFECEB] text-[#7A1712]",
  at_risk: "border-[#B47500] bg-[#FFF6E5] text-[#7A5200]",
  on_track: "border-[#0F7A3D] bg-[#EAF7EF] text-[#0B5C2E]",
  delivered_late: "border-slate-300 bg-slate-50 text-slate-600",
  closed: "border-slate-300 bg-slate-50 text-slate-600",
};

const StatCard = ({ label, value, tone, icon: Icon, testid }) => (
  <div data-testid={testid} className={`border bg-white p-4 ${tone === "bad" ? "border-[#B3241B]" : tone === "primary" ? "border-[#002FA7]" : "border-border"}`}>
    <div className="flex items-center justify-between">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      {Icon && <Icon size={16} className="text-slate-400" />}
    </div>
    <p className="mt-2 text-2xl font-bold text-[#0F172A]">{value}</p>
  </div>
);

const fmt = (value) => {
  if (!value) return "—";
  const d = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};
const hours = (mins) => (!mins ? "—" : `${Math.floor(mins / 60)}h ${mins % 60}m`);

const BoardRow = ({ row, onRetime, index }) => (
  <div data-testid={`ft-row-${index}`} className="border border-border bg-white p-4">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div>
        <p className="font-semibold text-[#0F172A]">{row.shipment_number} · {row.lane}</p>
        <p className="text-xs text-slate-500">{row.carrier || "Unassigned"}{row.driver_name ? ` · ${row.driver_name}` : ""} · {row.status?.replace(/_/g, " ")}</p>
      </div>
      <span className={`border px-2 py-1 text-xs font-semibold ${STATE_TONE[row.timing.state]}`} data-testid={`ft-state-${index}`}>
        {STATE_LABEL[row.timing.state]}
      </span>
    </div>
    {row.timing.reason && <p className="mt-2 text-sm text-slate-600">{row.timing.reason}</p>}
    <div className="mt-3 grid gap-2 text-xs text-slate-500 sm:grid-cols-2 lg:grid-cols-4">
      <p>Pick-up promised: <span className="text-[#0F172A]">{fmt(row.timing.promised_pickup_to)}</span></p>
      <p>Collected: <span className="text-[#0F172A]">{fmt(row.timing.actual_pickup_at)}</span></p>
      <p>Delivery promised: <span className="text-[#0F172A]">{fmt(row.timing.promised_delivery_to)}</span></p>
      <p>Delivered: <span className="text-[#0F172A]">{fmt(row.timing.actual_delivered_at)}</span></p>
    </div>
    <Button variant="outline" size="sm" className="mt-3" data-testid={`ft-retime-${index}`} onClick={() => onRetime(row)}>
      Set promised times
    </Button>
  </div>
);

export default function FreightTime() {
  const { user } = useAuth();
  const [view, setView] = useState("board");
  const [board, setBoard] = useState(null);
  const [groupBy, setGroupBy] = useState("carrier");
  const [score, setScore] = useState(null);
  const [depots, setDepots] = useState([]);
  const [slots, setSlots] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [shiftData, setShiftData] = useState(null);
  const [turnData, setTurnData] = useState(null);
  const [retime, setRetime] = useState(null);
  const [slotForm, setSlotForm] = useState({ depot_id: "", dock: 1, kind: "delivery", slot_date: "", start_time: "09:00", end_time: "10:00", notes: "" });
  const [shiftForm, setShiftForm] = useState({ driver_id: "", shift_date: "", driving_minutes: 0, rest_minutes: 45, other_work_minutes: 0 });
  const [turnForm, setTurnForm] = useState({ site_name: "", kind: "delivery", arrived_at: "", departed_at: "" });

  const isManager = ["admin", "vendor"].includes(user?.role);

  const loadBoard = useCallback(() => {
    api.get("/freight-time/board").then(({ data }) => setBoard(data)).catch(() => setBoard(null));
  }, []);

  useEffect(loadBoard, [loadBoard]);
  useEffect(() => {
    api.get(`/freight-time/scorecard?group_by=${groupBy}`).then(({ data }) => setScore(data)).catch(() => setScore(null));
  }, [groupBy]);
  useEffect(() => {
    api.get("/depots").then(({ data }) => setDepots(data)).catch(() => setDepots([]));
    api.get("/drivers").then(({ data }) => setDrivers(data)).catch(() => setDrivers([]));
  }, []);

  const loadSlots = useCallback(() => {
    api.get("/freight-time/slots").then(({ data }) => setSlots(data)).catch(() => setSlots([]));
  }, []);
  const loadShifts = useCallback(() => {
    api.get("/freight-time/shifts?days=30").then(({ data }) => setShiftData(data)).catch(() => setShiftData(null));
  }, []);
  const loadTurns = useCallback(() => {
    api.get("/freight-time/turnarounds").then(({ data }) => setTurnData(data)).catch(() => setTurnData(null));
  }, []);
  useEffect(() => {
    if (view === "slots") loadSlots();
    if (view === "hours" && isManager) loadShifts();
    if (view === "turnaround") loadTurns();
  }, [view, loadSlots, loadShifts, loadTurns, isManager]);

  const attention = useMemo(
    () => (board ? [...board.late, ...board.pickup_overdue, ...board.at_risk] : []),
    [board]
  );

  const openRetime = (row) => {
    const local = (v) => (v ? String(v).slice(0, 16).replace(" ", "T") : "");
    setRetime({
      id: row.id,
      shipment_number: row.shipment_number,
      promised_pickup_from: local(row.timing.promised_pickup_from),
      promised_pickup_to: local(row.timing.promised_pickup_to),
      promised_delivery_from: local(row.timing.promised_delivery_from),
      promised_delivery_to: local(row.timing.promised_delivery_to),
    });
  };

  const saveWindow = async () => {
    try {
      await api.patch(`/freight-time/shipments/${retime.id}/window`, {
        promised_pickup_from: retime.promised_pickup_from || null,
        promised_pickup_to: retime.promised_pickup_to || null,
        promised_delivery_from: retime.promised_delivery_from || null,
        promised_delivery_to: retime.promised_delivery_to || null,
      });
      toast.success("Promised times saved");
      setRetime(null);
      loadBoard();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const bookSlot = async () => {
    try {
      await api.post("/freight-time/slots", { ...slotForm, dock: Number(slotForm.dock) });
      toast.success("Dock slot booked");
      setSlotForm({ ...slotForm, notes: "" });
      loadSlots();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const logShift = async () => {
    try {
      await api.post("/freight-time/shifts", {
        ...shiftForm,
        driving_minutes: Number(shiftForm.driving_minutes),
        rest_minutes: Number(shiftForm.rest_minutes),
        other_work_minutes: Number(shiftForm.other_work_minutes),
      });
      toast.success("Hours logged");
      loadShifts();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const logTurn = async () => {
    try {
      await api.post("/freight-time/turnarounds", turnForm);
      toast.success("Turnaround logged");
      setTurnForm({ ...turnForm, arrived_at: "", departed_at: "" });
      loadTurns();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const runScan = async () => {
    try {
      const { data } = await api.post("/freight-time/scan", {});
      toast.success(`${data.flagged} booking(s) flagged, ${data.alerted} alert(s) sent`);
      loadBoard();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="freight-time-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="overline text-[#B3241B]">Freight time management</p>
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#0F172A]">Promised times, delays and hours</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">
            Promised windows against what actually happened, a board of everything needing attention,
            dock slots so two loads never clash, and driver hours and site turnaround times.
          </p>
        </div>
        {user?.role === "admin" && (
          <Button variant="outline" data-testid="ft-scan" onClick={runScan}>
            <ArrowsClockwise size={16} className="mr-1.5" /> Run delay scan
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Needs attention" value={board?.needs_attention ?? "—"} tone={board?.needs_attention ? "bad" : undefined} icon={WarningCircle} testid="ft-stat-attention" />
        <StatCard label="Late now" value={board?.counts?.late ?? "—"} icon={Clock} testid="ft-stat-late" />
        <StatCard label="On-time (delivered)" value={score?.on_time_pct != null ? `${score.on_time_pct}%` : "—"} tone="primary" icon={Gauge} testid="ft-stat-ontime" />
        <StatCard label="No promised window" value={board?.no_window ?? "—"} icon={CalendarBlank} testid="ft-stat-nowindow" />
      </div>

      <Tabs value={view} onValueChange={setView} className="w-full scroll-x overflow-x-auto">
        <TabsList data-testid="ft-tabs" className="w-max">
          <TabsTrigger value="board" data-testid="ft-tab-board">Needs attention</TabsTrigger>
          <TabsTrigger value="all" data-testid="ft-tab-all">All bookings</TabsTrigger>
          <TabsTrigger value="scorecard" data-testid="ft-tab-scorecard">Scorecard</TabsTrigger>
          <TabsTrigger value="slots" data-testid="ft-tab-slots">Dock slots</TabsTrigger>
          <TabsTrigger value="hours" data-testid="ft-tab-hours">Driver hours</TabsTrigger>
          <TabsTrigger value="turnaround" data-testid="ft-tab-turnaround">Turnaround</TabsTrigger>
        </TabsList>
      </Tabs>

      {view === "board" && (
        <div className="space-y-3" data-testid="ft-board">
          {!board ? <p className="py-10 text-center text-sm text-slate-500">Loading…</p>
            : attention.length === 0 ? (
              <p data-testid="ft-board-empty" className="border border-border bg-white p-8 text-center text-sm text-slate-600">
                Nothing running late — every booking is inside its promised window.
              </p>
            ) : attention.map((row, i) => <BoardRow key={row.id} row={row} index={i} onRetime={openRetime} />)}
        </div>
      )}

      {view === "all" && (
        <div className="space-y-3" data-testid="ft-all">
          {[...attention, ...(board?.on_track || []), ...(board?.delivered_late || [])].map((row, i) => (
            <BoardRow key={row.id} row={row} index={i} onRetime={openRetime} />
          ))}
        </div>
      )}

      {view === "scorecard" && (
        <div className="space-y-4" data-testid="ft-scorecard">
          <div className="flex gap-2">
            {[["carrier", "By carrier"], ["lane", "By lane"]].map(([key, label]) => (
              <button key={key} data-testid={`ft-group-${key}`} onClick={() => setGroupBy(key)}
                className={`border px-3 py-2 text-xs font-semibold ${groupBy === key ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-border bg-white text-slate-600"}`}>
                {label}
              </button>
            ))}
          </div>
          {!score || score.rows.length === 0 ? (
            <p data-testid="ft-scorecard-empty" className="border border-border bg-white p-8 text-center text-sm text-slate-600">
              No delivered bookings with a promised window yet — set windows and the scorecard fills in.
            </p>
          ) : (
            <>
            <div className="space-y-2 md:hidden" data-testid="ft-score-cards">
              {score.rows.map((r, i) => (
                <div key={r.name} data-testid={`ft-score-card-${i}`} className="border border-border bg-white p-4">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-[#0F172A]">{r.name}</p>
                    <span className={`text-sm font-semibold ${r.on_time_pct >= 90 ? "text-[#0B5C2E]" : r.on_time_pct >= 70 ? "text-[#7A5200]" : "text-[#B3241B]"}`}>{r.on_time_pct}% on time</span>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-500">
                    <p>Delivered: <span className="text-[#0F172A]">{r.delivered}</span></p>
                    <p>Late: <span className="text-[#0F172A]">{r.late}</span></p>
                    <p>Avg late: <span className="text-[#0F172A]">{r.avg_late_minutes ? hours(r.avg_late_minutes) : "—"}</span></p>
                    <p>Avg transit: <span className="text-[#0F172A]">{r.avg_transit_hours != null ? `${r.avg_transit_hours}h` : "—"}</span></p>
                  </div>
                </div>
              ))}
            </div>
            <div className="hidden overflow-x-auto border border-border bg-white md:block">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="p-3 text-left">{groupBy === "carrier" ? "Carrier" : "Lane"}</th>
                    <th className="p-3 text-right">Delivered</th>
                    <th className="p-3 text-right">On time</th>
                    <th className="p-3 text-right">Late</th>
                    <th className="p-3 text-right">On-time %</th>
                    <th className="p-3 text-right">Avg late</th>
                    <th className="p-3 text-right">Avg transit</th>
                  </tr>
                </thead>
                <tbody data-testid="ft-scorecard-rows">
                  {score.rows.map((r, i) => (
                    <tr key={r.name} className="border-t border-border" data-testid={`ft-score-${i}`}>
                      <td className="p-3 font-semibold text-[#0F172A]">{r.name}</td>
                      <td className="p-3 text-right">{r.delivered}</td>
                      <td className="p-3 text-right">{r.on_time}</td>
                      <td className="p-3 text-right">{r.late}</td>
                      <td className={`p-3 text-right font-semibold ${r.on_time_pct >= 90 ? "text-[#0B5C2E]" : r.on_time_pct >= 70 ? "text-[#7A5200]" : "text-[#B3241B]"}`}>{r.on_time_pct}%</td>
                      <td className="p-3 text-right">{r.avg_late_minutes ? hours(r.avg_late_minutes) : "—"}</td>
                      <td className="p-3 text-right">{r.avg_transit_hours != null ? `${r.avg_transit_hours}h` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
          {score?.unmeasured > 0 && (
            <p className="text-xs text-slate-500" data-testid="ft-unmeasured">
              {score.unmeasured} delivered booking(s) had no promised window, so they are not scored.
            </p>
          )}
        </div>
      )}

      {view === "slots" && (
        <div className="space-y-4" data-testid="ft-slots">
          <div className="border border-border bg-white p-4">
            <p className="text-sm font-semibold text-[#0F172A]">Book a dock slot</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
              <div>
                <Label>Depot</Label>
                <select data-testid="ft-slot-depot" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm" value={slotForm.depot_id} onChange={(e) => setSlotForm({ ...slotForm, depot_id: e.target.value })}>
                  <option value="">Pick a depot</option>
                  {depots.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div><Label>Dock</Label><Input data-testid="ft-slot-dock" type="number" min={1} value={slotForm.dock} onChange={(e) => setSlotForm({ ...slotForm, dock: e.target.value })} /></div>
              <div>
                <Label>Kind</Label>
                <select data-testid="ft-slot-kind" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm" value={slotForm.kind} onChange={(e) => setSlotForm({ ...slotForm, kind: e.target.value })}>
                  <option value="delivery">Delivery</option>
                  <option value="pickup">Pick-up</option>
                </select>
              </div>
              <div><Label>Date</Label><Input data-testid="ft-slot-date" type="date" value={slotForm.slot_date} onChange={(e) => setSlotForm({ ...slotForm, slot_date: e.target.value })} /></div>
              <div><Label>From</Label><Input data-testid="ft-slot-start" type="time" value={slotForm.start_time} onChange={(e) => setSlotForm({ ...slotForm, start_time: e.target.value })} /></div>
              <div><Label>To</Label><Input data-testid="ft-slot-end" type="time" value={slotForm.end_time} onChange={(e) => setSlotForm({ ...slotForm, end_time: e.target.value })} /></div>
            </div>
            <Button className="mt-3" data-testid="ft-slot-submit" onClick={bookSlot}>Book the slot</Button>
          </div>
          {slots.length === 0 ? (
            <p data-testid="ft-slots-empty" className="border border-border bg-white p-8 text-center text-sm text-slate-600">No dock slots booked yet.</p>
          ) : (
            <div className="space-y-2" data-testid="ft-slot-list">
              {slots.map((s, i) => (
                <div key={s.id} data-testid={`ft-slot-${i}`} className="flex flex-wrap items-center justify-between gap-2 border border-border bg-white p-3 text-sm">
                  <span className="font-semibold text-[#0F172A]">{s.depot_name} · dock {s.dock}</span>
                  <span className="text-slate-600">{s.slot_date} · {s.start_time}–{s.end_time} · {s.kind}</span>
                  <span className="text-xs text-slate-500">{s.shipment_number || s.booked_by_name}</span>
                  <button data-testid={`ft-slot-release-${i}`} className="text-xs font-semibold text-slate-500 hover:text-[#B3241B]"
                    onClick={async () => {
                      try { await api.delete(`/freight-time/slots/${s.id}`); loadSlots(); toast.success("Slot released"); }
                      catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
                    }}>Release</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {view === "hours" && (
        <div className="space-y-4" data-testid="ft-hours">
          {isManager && (
            <div className="border border-border bg-white p-4">
              <p className="text-sm font-semibold text-[#0F172A]">Log a driver's day</p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                <div>
                  <Label>Driver</Label>
                  <select data-testid="ft-shift-driver" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm" value={shiftForm.driver_id} onChange={(e) => setShiftForm({ ...shiftForm, driver_id: e.target.value })}>
                    <option value="">Pick a driver</option>
                    {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
                <div><Label>Date</Label><Input data-testid="ft-shift-date" type="date" value={shiftForm.shift_date} onChange={(e) => setShiftForm({ ...shiftForm, shift_date: e.target.value })} /></div>
                <div><Label>Driving (min)</Label><Input data-testid="ft-shift-driving" type="number" min={0} value={shiftForm.driving_minutes} onChange={(e) => setShiftForm({ ...shiftForm, driving_minutes: e.target.value })} /></div>
                <div><Label>Rest (min)</Label><Input data-testid="ft-shift-rest" type="number" min={0} value={shiftForm.rest_minutes} onChange={(e) => setShiftForm({ ...shiftForm, rest_minutes: e.target.value })} /></div>
                <div><Label>Other work (min)</Label><Input data-testid="ft-shift-other" type="number" min={0} value={shiftForm.other_work_minutes} onChange={(e) => setShiftForm({ ...shiftForm, other_work_minutes: e.target.value })} /></div>
              </div>
              <Button className="mt-3" data-testid="ft-shift-submit" onClick={logShift}>Save the hours</Button>
            </div>
          )}
          {isManager ? (
            !shiftData || shiftData.drivers.length === 0 ? (
              <p data-testid="ft-hours-empty" className="border border-border bg-white p-8 text-center text-sm text-slate-600">No driver hours logged in the last 30 days.</p>
            ) : (
              <div className="space-y-2" data-testid="ft-driver-list">
                {shiftData.drivers.map((d, i) => (
                  <div key={d.driver_id} data-testid={`ft-driver-${i}`} className="border border-border bg-white p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="font-semibold text-[#0F172A]">{d.driver_name}</p>
                      <p className="text-sm text-slate-600">{d.driving_hours}h driving over {d.days} day(s) · avg {d.avg_driving_hours}h</p>
                    </div>
                    {d.breaches.length > 0 && (
                      <ul data-testid={`ft-driver-breaches-${i}`} className="mt-2 space-y-1 border-l-2 border-[#B3241B] bg-[#FFECEB] px-3 py-2 text-xs text-[#7A1712]">
                        {d.breaches.map((b) => <li key={b}>{b}</li>)}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )
          ) : (
            <p data-testid="ft-hours-restricted" className="border border-border bg-white p-8 text-center text-sm text-slate-600">
              Driver hours are visible to admins and carriers only.
            </p>
          )}
        </div>
      )}

      {view === "turnaround" && (
        <div className="space-y-4" data-testid="ft-turnaround">
          <div className="border border-border bg-white p-4">
            <p className="text-sm font-semibold text-[#0F172A]">Log a site visit</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div><Label>Site</Label><Input data-testid="ft-turn-site" value={turnForm.site_name} onChange={(e) => setTurnForm({ ...turnForm, site_name: e.target.value })} placeholder="Depot, yard or customer site" /></div>
              <div>
                <Label>Kind</Label>
                <select data-testid="ft-turn-kind" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm" value={turnForm.kind} onChange={(e) => setTurnForm({ ...turnForm, kind: e.target.value })}>
                  <option value="delivery">Delivery</option>
                  <option value="pickup">Pick-up</option>
                </select>
              </div>
              <div><Label>Arrived</Label><Input data-testid="ft-turn-arrived" type="datetime-local" value={turnForm.arrived_at} onChange={(e) => setTurnForm({ ...turnForm, arrived_at: e.target.value })} /></div>
              <div><Label>Left</Label><Input data-testid="ft-turn-departed" type="datetime-local" value={turnForm.departed_at} onChange={(e) => setTurnForm({ ...turnForm, departed_at: e.target.value })} /></div>
            </div>
            <Button className="mt-3" data-testid="ft-turn-submit" onClick={logTurn}>Save the visit</Button>
          </div>
          {!turnData || turnData.sites.length === 0 ? (
            <p data-testid="ft-turn-empty" className="border border-border bg-white p-8 text-center text-sm text-slate-600">No turnaround times logged yet.</p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2" data-testid="ft-site-list">
              {turnData.sites.map((s, i) => (
                <div key={s.site_name} data-testid={`ft-site-${i}`} className="border border-border bg-white p-4">
                  <p className="font-semibold text-[#0F172A]">{s.site_name}</p>
                  <p className="mt-1 text-sm text-slate-600">
                    <Timer size={14} className="mr-1 inline" /> {s.avg_minutes} min average across {s.visits} visit(s) · worst {s.worst_minutes} min
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Dialog open={!!retime} onOpenChange={(o) => !o && setRetime(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle>Promised times — {retime?.shipment_number}</DialogTitle></DialogHeader>
          {retime && (
            <div className="space-y-3">
              <div><Label>Pick-up window from</Label><Input data-testid="ft-window-pickup-from" type="datetime-local" value={retime.promised_pickup_from || ""} onChange={(e) => setRetime({ ...retime, promised_pickup_from: e.target.value })} /></div>
              <div><Label>Pick-up window to</Label><Input data-testid="ft-window-pickup-to" type="datetime-local" value={retime.promised_pickup_to || ""} onChange={(e) => setRetime({ ...retime, promised_pickup_to: e.target.value })} /></div>
              <div><Label>Delivery window from</Label><Input data-testid="ft-window-delivery-from" type="datetime-local" value={retime.promised_delivery_from || ""} onChange={(e) => setRetime({ ...retime, promised_delivery_from: e.target.value })} /></div>
              <div><Label>Delivery window to</Label><Input data-testid="ft-window-delivery-to" type="datetime-local" value={retime.promised_delivery_to || ""} onChange={(e) => setRetime({ ...retime, promised_delivery_to: e.target.value })} /></div>
              <Button className="w-full" data-testid="ft-window-save" onClick={saveWindow}>
                <Truck size={16} className="mr-1.5" /> Save promised times
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
